import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getThunkAndSlice } from "@src/store/getThunkAndSlice";
import { getInitialState } from "@src/store/helpers";
import { EventInventoryRecord, queryEventInventoryAll } from "./queryEventInventoryAllSlice";
import { ApiProvider } from "../ApiProvider";
import { hiddenQueryEventsSlice } from "./queryEventsSlice";
import { SEARCH_ALL_DATES_FROM } from "@src/components/utils/smartSearchUtils";

export interface IGlobalInventoryRawItem extends EventInventoryRecord {
	Listing_Face?: number;
	Num_Barcodes?: number;
	PdfsAttached?: number;
	Combined_Tags?: string;
	Seats: string;
	Num_OnHold?: number;
	Num_Consignor_Hold?: number;
	Cat_ID?: number;
	Cat_Desc?: string;
	Cat_Type?: string;
	Event_Name?: string;
	Event_Headliner?: string;
	Event_Headliner_ID?: number;
	Event_Date?: string;
	Event_Time?: string;
	Venue_ID?: number;
	Venue_Name?: string;
	Num_PDFs?: number;
	Num_Ticket_IDs?: number;
	TicketNetwork_Event_ID?: number;
	TicketsNow_Production_ID?: number;
	Vivid_Production_ID?: number | null;
}

export interface IGlobalInventoryParams {
	SearchString?: string | null;
	EventFromDate?: string | null;
	EventToDate?: string | null;
	POFromDate?: string | null;
	POToDate?: string | null;
	CategoryId?: number | null;
	EventHeadlinerId?: number | null;
	EventId?: number | null;
	VenueId?: number | null;
	ListingId?: number | null;
	POId?: number | null;
	Sections?: string | null;
	Row?: string | null;
	Quantity?: number | null;
	DaysSinceLastUpdate?: number | null;
	CompanyName?: string | null;
	ForceRefresh?: boolean;
	ShowSold?: boolean | null;
	ShowOnHold?: boolean | null;
	SearchTag?: string | null;
	page?: number;
	startIndex?: number;
	limit?: number;
}

const initialState = new Array<EventInventoryRecord>();
const iState = getInitialState(initialState);

export const { thunk: queryGlobalInventory, pathKey: globalInventoryPathKey } = getThunkAndSlice<
	IGlobalInventoryParams,
	{ Inventory: typeof initialState },
	typeof initialState
>({
	name: "_api_Pricing_QueryGlobalInventory",
	initialState,
	path: "/api/Pricing/QueryInventory",
	responsePath: "Inventory",
	method: "post",
});

export const getGlobalInventoryParams = (companyName: string | null | undefined, skipCache = false, searchString = "") => {
	const baseCacheKey = companyName || "cache_all";
	return {
		CompanyName: companyName || null,
		SearchString: searchString || null,
		...(searchString ? { EventFromDate: SEARCH_ALL_DATES_FROM } : {}),
		ForceRefresh: true,
		ShowSold: true,
		ShowOnHold: true,
		cacheKey: searchString ? `${baseCacheKey}_search:${searchString.toLocaleLowerCase()}` : baseCacheKey,
		skipCache,
	};
};

export const queryGlobalInventorySlice = createSlice({
	name: "_api_Pricing_QueryGlobalInventory",
	initialState: { ...iState, currentCacheKey: "" },
	reducers: {
		updateGlobalInventory: (state, action: PayloadAction<{ data: Array<EventInventoryRecord> }>) => {
			state.data = action.payload.data;
			state.version++;
			if (state.currentCacheKey) {
				ApiProvider.default.setCache(globalInventoryPathKey, state.currentCacheKey, action.payload.data);
			}
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(queryEventInventoryAll.pending, (_state, action) => {
				if (!action.meta.arg.skipCache) return;
				ApiProvider.default.removeCachePath(globalInventoryPathKey);
				ApiProvider.default.removeCachePath(hiddenQueryEventsSlice.name);
			})
			.addCase(queryGlobalInventory.pending, (state) => {
				state.loading = true;
				state.isInitialLoad = false;
			})
			.addCase(queryGlobalInventory.fulfilled, (state, action) => {
				state.loading = false;
				state.version++;
				state.init = true;
				state.data = action.payload as typeof initialState;
				state.error = null;
				state.currentCacheKey = (action.meta.arg as IGlobalInventoryParams & { cacheKey?: string }).cacheKey ?? "";
			})
			.addCase(queryGlobalInventory.rejected, (state, action) => {
				if (action.meta.aborted) return;
				state.loading = false;
				state.data = iState.data;
				const payload = action.payload as { Message?: string } | string | null | undefined;
				if (action.error?.message) {
					state.error = action.error.message;
				} else if (typeof payload === "object" && payload !== null && payload.Message) {
					state.error = payload.Message;
				} else if (typeof payload === "string") {
					state.error = payload;
				} else {
					state.error = JSON.stringify(action.payload);
				}
			});
	},
});
